<template>
  <div>
    <strong v-if="title">{{ title }}</strong>
    <address>
      <a :href="'mailto:'+ address.email">{{ address.name }}</a><br>
      {{ address.street }} {{ address.number }}<br />
      {{ address.neighborhood }}, {{ address.city }}<br>
      {{ address.state }}, {{ address.country_name }} {{ address.postal_code }}<br>
      {{ address.phone }}
    </address>

    <v-btn color="primary" dark small absolute bottom right fab v-if="editable" @click="edit">
      <v-icon>edit</v-icon>
    </v-btn>

    <v-layout row justify-center v-if="editable">
      <v-dialog v-model="dialog" persistent max-width="600px">
        <v-card>
          <v-card-title>
            <v-container fluid grid-list-xs>
              <v-layout row wrap>
                <v-flex xs12>
                  <h1 class="title">
                    {{ title }}
                  </h1>
                </v-flex>

                <v-flex xs12>
                  <v-text-field v-model="address.name"
                                name="name"
                                :label="$t('attrs.name')"
                                v-validate="'required|max:35'"
                                :error-messages="errors.collect('name')"
                                data-vv-name="name"
                                :data-vv-as="$t('attrs.name')"
                                required
                                ></v-text-field>
                </v-flex>

                <v-flex xs12 md4>
                  <v-text-field v-model="address.email"
                                name="email"
                                :label="$t('attrs.email')"
                                v-validate="'required|email'"
                                :error-messages="errors.collect('email')"
                                data-vv-name="email"
                                :data-vv-as="$t('attrs.email')"
                                :disabled="disableEmail"
                                required
                                ></v-text-field>
                </v-flex>

                <v-flex xs12 md8 class="pt-0 pb-0">
                  <vue-phone-number-input v-model="phone"
                                          name="phone"
                                          :no-example="true"
                                          :error-color="'#FF5252'"
                                          :valid-color="'#476AFE'"
                                          :default-country-code="address.country_code || 'MX'"
                                          @update="getPayload"
                                          v-validate="`required|destPhoneValidation:${address.phone}`"
                                          :error-messages="errors.collect('phone')"
                                          data-vv-name="phone"
                                          :data-vv-as="$t('attrs.phone')"
                                          :disabled="disablePhone"
                                          :preferred-countries="['MX', 'US']"
                                          ></vue-phone-number-input>

                  <div class="v-text-field__details">
                    <div class="v-messages theme--light error--text">
                      <div class="v-messages__wrapper">
                        <div class="v-messages__message message-transition-enter-to" style="">
                          {{ errors.first('phone') }}
                        </div>
                      </div>
                    </div>
                  </div>
                </v-flex>

                <v-flex xs12 md6>
                  <v-text-field v-model="address.company"
                                name="company"
                                :label="$t('attrs.company')"
                                v-validate="'required'"
                                :error-messages="errors.collect('company')"
                                data-vv-name="company"
                                :data-vv-as="$t('attrs.company')"
                                required
                                ></v-text-field>
                </v-flex>

                <v-flex xs12 md6>
                  <v-text-field v-model="address.rfc"
                                name="rfc"
                                :label="$t('attrs.rfc')"
                                ></v-text-field>
                </v-flex>

                <v-flex xs12 md6>
                  <v-autocomplete v-model="address.country_id"
                        :items="countries"
                        item-value="id"
                        item-text="name"
                        :label="$tc('models.country')"
                        @change="fetchStates"
                        v-validate="'required'"
                        :error-messages="errors.collect('country_id')"
                        data-vv-name="country_id"
                        :data-vv-as="$tc('models.country')"
                        browser-autocomplete="hydra-new-password"
                        required
                        ></v-autocomplete>
                </v-flex>

                <v-flex xs12 md6>
                  <v-combobox v-model="address.state"
                            :items="states"
                            item-value="name"
                            item-text="name"
                            :return-object="false"
                            v-validate="'required'"
                            :label="$tc('models.state')"
                            :disabled="!address.country_id"
                            :error-messages="errors.collect('state')"
                            data-vv-name="state"
                            :data-vv-as="$t('attrs.state')"
                            required
                            ></v-combobox>
                </v-flex>

                <v-flex xs12 md6>
                  <v-text-field v-model="address.street"
                                name="street"
                                :label="$t('attrs.street')"
                                v-validate="'required|max:30'"
                                :error-messages="errors.collect('street')"
                                data-vv-name="street"
                                :data-vv-as="$t('attrs.street')"
                                required
                                ></v-text-field>
                </v-flex>

                <v-flex xs12 md6>
                  <v-text-field v-model="address.number"
                                name="number"
                                :label="$t('attrs.number')"
                                v-validate="'required'"
                                :error-messages="errors.collect('number')"
                                data-vv-name="number"
                                :data-vv-as="$t('attrs.number')"
                                required
                                ></v-text-field>
                </v-flex>

                <v-flex xs12 md6 class="pt-0 pb-0">
                  <v-text-field v-model="address.neighborhood"
                            item-value="name"
                            item-text="name"
                            :label="$tc('attrs.neighborhood')"
                            v-validate="'required'"
                            :error-messages="errors.collect('neighborhood')"
                            data-vv-name="neighborhood"
                            :data-vv-as="$t('attrs.neighborhood')"
                            required
                            ></v-text-field>
                </v-flex>

                <v-flex xs12 md6>
                  <v-text-field v-model="address.city"
                                name="city"
                                :label="$t('attrs.city')"
                                v-validate="'required|max:35'"
                                :error-messages="errors.collect('city')"
                                data-vv-name="city"
                                :data-vv-as="$t('attrs.city')"
                                required
                                ></v-text-field>
                </v-flex>
                <v-flex xs12 md6>
                  <v-text-field v-model="address.postal_code"
                                name="postal_code"
                                :label="$t('attrs.postal_code')"
                                v-validate="'required|max:7'"
                                :error-messages="errors.collect('postal_code')"
                                data-vv-name="postal_code"
                                :data-vv-as="$t('attrs.postal_code')"
                                @change="fetchColonies"
                                required
                                ></v-text-field>
                </v-flex>

                <v-flex xs12>
                  <v-text-field v-model="address.reference"
                                name="reference"
                                :label="`${$t('attrs.reference')} (${$t('optional')})`"
                                v-validate="'max:35'"
                              :error-messages="errors.collect('reference')"
                              data-vv-name="reference"
                              :data-vv-as="$t('attrs.reference')"
                                ></v-text-field>
                </v-flex>

                <v-flex xs12 class="pt-0 pb-0">
                  <v-checkbox v-model="address.save_new" name="save_new" :label="$t('saveAddress')"></v-checkbox>
                </v-flex>

              </v-layout>
            </v-container>
          </v-card-title>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" flat @click="dialog = false" :loading="loading" :disabled="loading">{{ $t('cancel') }}</v-btn>
            <v-btn color="primary" dark @click="save"  :loading="loading" :disabled="loading" type="submit">{{ $t('save') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-layout>
  </div>
</template>

<script>
import Country from '@/client/country'
import State from '@/client/state'

// import Address from '@/client/address'
import axios from 'axios'

import VuePhoneNumberInput from 'vue-phone-number-input'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'

export default {
  name: 'ShipmentAddressComp',

  props: {
    title: String,

    address: {
      type: Object,
      required: true
    },

    editable: {
      type: Boolean,
      default: false
    },
    addressType: {
      type: String
    },
    disableEmail: {
      type: Boolean,
      default: false
    },
    disablePhone: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      countries: [],
      states: [],
      loading: false,
      dialog: false,
      codeAddress: '',
      country: '',
      subLocalities: [],
      doneFirst: false,
      phone: '',
      onlyCountries: [],
    }
  },

  components: {
    VuePhoneNumberInput
  },

  computed: {
    selectedCountry () {
      return this.countries.find(c => c.id === this.address.country_id) || {}
    },

    isMexico () {
      return this.selectedCountry.code === 'MX'
    },
  },

  methods: {
    async fetchStates () {
      this.states = []
      this.states = await State.all({
        where: { country_id: this.address.country_id },
        order: 'name ASC',
        per_page: 2000
      })
    },

    edit () {
      this.dialog = true
    },

    async save () {
      this.spanishizeAddress()

      const isValid = await this.$validator.validateAll()
      if (!isValid) { return }

      this.loading = true
      try {
        await this.address.save()
        this.dialog = false
        this.$emit('fetch')
      } catch (err) {
        this.handleErr(err)
      }
      this.loading = false
    },


    async setCityState(response) {
      try {
        this.clearCityState()
        if (this.states.length == 0) await this.fetchStates()

        let responseState = this.setGeocodes(response, 'administrative_area_level_1')

        this.address.state = this.states.find( state => state.name.spanishize().toLowerCase() == responseState ).name
        this.address.city = this.setGeocodes(response, 'locality')

        this.setSubLocalities(response)
      } catch (err) {
        // console.log('error', err)
      }
    },

    setGeocodes (res, nameType) {
      if (res) {
        return res.address_components.find( comp => comp.types.includes(nameType) ).long_name.spanishize().toLowerCase()
      }
    },

    setSubLocalities(response) {
      this.subLocalities = []
      if (response && response.postcode_localities) {
        response.postcode_localities.forEach( (value) => {
          this.subLocalities.push({ name: value })
        })
      } else {
        this.subLocalities.push({ name: this.setGeocodes(response, 'sublocality') })
      }

      return this.subLocalities
    },

    // async getGeoCodes () {
    //   if (this.address.postal_code.length == 5) {
    //     this.country = this.countries.find( country => country.id == this.address.country_id )
    //     this.codeAddress = `${ this.address.postal_code } ${ this.country.name.spanishize() }`

    //     let response = await Address.zipCodeAutoFill(this.codeAddress)
    //     this.setCityState( response )
    //   }
    // },

    async fetchColonies () {
      if (!this.isMexico) { return }
      if (!this.address.postal_code) { return }

      this.loading = true
      try {
        const url = 'https://sat.api.hydraship.app/api/colonies'
        const res = await axios.get(url, { params: { postal_code: this.address.postal_code } })
        this.colonies = res.data.data
        if (this.colonies.length) {
          this.address.city = this.colonies[0].city
          this.address.state = this.colonies[0].state
        }
      } catch (e) {
        this.handleErr(e)
      }
      this.loading = false
    },

    clearCityState () {
      this.address.city = ''
      this.address.state = ''
      this.address.neighborhood = ''
      this.subLocalities = []
    },

    getPayload(payload) {
      if (payload.countryCallingCode) this.address.calling_code = payload.countryCallingCode
      this.address.phone = payload.nationalNumber
    },

    spanishizeAddress () {
      const keys = ['name', 'company', 'email', 'reference', 'street', 'neighborhood', 'city']

      for( let i = 0; i < keys.length; ++i ) {
        if (this.address[keys[i]]) this.address[keys[i]] = this.address[keys[i]].spanishize()
      }
    }
  },

  watch: {
    'address.phone' (val) {
      this.phone = val
    },

    'address.reference' (val) {
      if (val.length > 35) {
        alert('La referencia no puede tener más de 35 caracteres')
        this.address.reference = val.slice(0, 35)
      }
    },
  },

  async mounted () {
    this.countries = await Country.all({ per_page: 2000, fast_search: true })
    await this.fetchStates()
    this.phone = this.address.phone
  }
}
</script>
